import type {
  EditorState,
  UndoableState,
} from "../../vendor/src/features/remotion-editor/editor/state/types";
import type { AudioItem } from "../../vendor/src/features/remotion-editor/editor/items/audio/audio-item-type";
import { hasPendingTimelineEdit } from "./edit-ownership";

export function isMergedAudio(doc: UndoableState, id: string) {
  const item = doc.items[id];
  return (
    item?.type === "group" &&
    item.children.length > 0 &&
    item.children.every((key) => doc.items[key]?.type === "audio")
  );
}
export function selectedAudioForMerge(state: EditorState, clicked: string) {
  const ids = state.selectedItems.includes(clicked)
    ? state.selectedItems
    : [clicked];
  return [...new Set(ids)].filter(
    (id) =>
      state.undoableState.items[id]?.type === "audio" ||
      isMergedAudio(state.undoableState, id),
  );
}
export function mergeAudio(
  state: EditorState,
  selected: readonly string[],
  allocate: () => string,
): EditorState {
  if (hasPendingTimelineEdit(state))
    throw Error("이동이나 시간 입력을 마친 뒤 병합해 주세요.");
  const doc = state.undoableState,
    ids = [...new Set(selected)];
  if (ids.length < 2)
    throw Error("병합할 음성 클립을 두 개 이상 선택해 주세요.");
  const roots = ids.map((id) => doc.items[id]);
  if (
    roots.some(
      (item) =>
        !item || (item.type !== "audio" && !isMergedAudio(doc, item.id)),
    )
  )
    throw Error("음성 클립만 선택해 주세요.");
  const owners = ids.map((id) =>
    doc.tracks.filter((t) => t.items.includes(id)),
  );
  if (owners.some((matches) => matches.length !== 1 || matches[0]!.locked))
    throw Error("그룹 내부 음성은 먼저 병합 해제하고 트랙 잠금을 풀어 주세요.");
  const first = owners[0]![0]!;
  if (
    owners.some(
      (matches) =>
        matches[0]!.muted !== first.muted ||
        matches[0]!.hidden !== first.hidden,
    )
  )
    throw Error(
      "선택한 음성 트랙의 음소거·숨김 상태를 같게 맞춘 뒤 병합해 주세요.",
    );
  if (
    Object.values(doc.transitions).some(
      (t) => ids.includes(t.sourceItemId) || ids.includes(t.targetItemId),
    )
  )
    throw Error("선택한 음성의 전환 효과를 해제한 뒤 병합해 주세요.");
  const children = roots.flatMap((item) =>
    item!.type === "group" ? item!.children : [item!.id],
  );
  if (new Set(children).size !== children.length)
    throw Error("겹치는 그룹 선택을 해제해 주세요.");
  if(Object.values(doc.transitions).some(t=>children.includes(t.sourceItemId)||children.includes(t.targetItemId)))throw Error('음성 내부의 전환 효과를 해제한 뒤 병합해 주세요.');
  const audio = children.map((id) => doc.items[id] as AudioItem);
  const from = Math.min(...roots.map((item) => item!.from));
  const end = Math.max(
    ...roots.map((item) => item!.from + item!.durationInFrames),
  );
  const groupId = allocate(),
    trackId = allocate();
  if (
    doc.items[groupId] ||
    doc.tracks.some((t) => t.id === trackId) ||
    groupId === trackId
  )
    throw Error("클립 식별자를 다시 만들어 주세요.");
  const items = { ...doc.items };
  for (const item of roots)
    if (item!.type === "group") {
      if (item!.animations?.length || item!.positionKeyframes?.length)
        throw Error("효과가 있는 병합 클립은 먼저 병합 해제해 주세요.");
      // Trimmed compounds may contain padded time; do not revive trimmed-away audio on flattening.
      if (
        item!.children.some((key) => {
          const child = doc.items[key]!;
          return (
            child.from < item!.from ||
            child.from + child.durationInFrames >
              item!.from + item!.durationInFrames
          );
        })
      )
        throw Error("잘린 병합 클립은 먼저 병합 해제한 뒤 다시 병합해 주세요.");
      delete items[item!.id];
    }
  items[groupId] = {
    id: groupId,
    type: "group",
    preserveGroupFrame: true,
    children: audio
      .sort((a, b) => a.from - b.from || a.id.localeCompare(b.id))
      .map((a) => a.id),
    from,
    durationInFrames: end - from,
    left: 0,
    top: 0,
    width: 1,
    height: 1,
    opacity: 1,
    isDraggingInTimeline: false,
  };
  return {
    ...state,
    selectedItems: [groupId],
    activeGroupId: null,
    inspectedGroupMemberId: null,
    undoableState: {
      ...doc,
      items,
      tracks: [
        ...doc.tracks.map((t) => ({
          ...t,
          items: t.items.filter((id) => !ids.includes(id)),
        })),
        {
          id: trackId,
          items: [groupId],
          locked: false,
          hidden: first.hidden,
          muted: first.muted,
          magnetic: false,
          zone: "audio",
          metadata: { type: "audio", isMainTrack: false, trackType: null },
        },
      ],
    },
  };
}
export function unmergeAudio(state: EditorState, id: string): EditorState {
  if (hasPendingTimelineEdit(state))
    throw Error("이동이나 시간 입력을 마친 뒤 해제해 주세요.");
  const doc = state.undoableState,
    group = doc.items[id];
  if (group?.type !== "group" || !isMergedAudio(doc, id))
    throw Error("병합된 음성 클립을 선택해 주세요.");
  const owners = doc.tracks.filter((t) => t.items.includes(id));
  if (owners.length !== 1 || owners[0]!.locked)
    throw Error("병합 음성 트랙의 잠금을 풀어 주세요.");
  if (group.animations?.length || group.positionKeyframes?.length)
    throw Error("병합 클립의 효과를 해제한 뒤 음성을 나눠 주세요.");
  if (
    group.children.some((key) => {
      const item = doc.items[key]!;
      return (
        item.from < group.from ||
        item.from + item.durationInFrames > group.from + group.durationInFrames
      );
    })
  )
    throw Error("잘린 클립은 자르기를 되돌린 뒤 병합 해제해 주세요.");
  if(Object.values(doc.transitions).some(t=>t.sourceItemId===id||t.targetItemId===id))throw Error('병합 클립의 전환 효과를 해제한 뒤 나눠 주세요.');
  const items = { ...doc.items };
  delete items[id];
  return {
    ...state,
    selectedItems: [...group.children],
    activeGroupId: null,
    inspectedGroupMemberId: null,
    undoableState: {
      ...doc,
      items,
      tracks: doc.tracks.map((t) =>
        t.id === owners[0]!.id
          ? {
              ...t,
              items: t.items.flatMap((key) =>
                key === id ? group.children : [key],
              ),
            }
          : t,
      ),
    },
  };
}
